'use client';

import { c as _c } from "react/compiler-runtime";
import { useOnType } from '@/globals/helpers/useOnType';
import { searchIcon } from '@/uiIcons/searchIcon.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { icon } from '@/uiPrimitives/media/icon';
import { textBlock } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { useInteractOutside } from '@/globals/helpers/useInteractOutside';
import { SuggestType, SourceDataItem } from './Autocomplete.types';
import { pile } from '@/uiPrimitives/layout/pile';
import { hourglassIcon } from '@/uiIcons/hourglassIcon.css';
import { AutocompleteSuggestions } from './AutocompleteSuggestions';
import { position } from '@/uiPrimitives/layout/position';
import { ErrorMessage } from '@/sections/errorMessage/ErrorMessage';
import { stack } from '@/uiPrimitives/layout/stack';
import { useFormStatus } from 'react-dom';
import { AutocompleteFormState } from './Autocomplete';
import { ChangeEvent, FocusEvent, useRef, useActionState, useState, RefObject } from 'react';
import { translateInline } from '@/globals/translate/translateInline';
import { appStateTranslations } from '@/globals/translate/appStateTranslations';
export type AutocompleteProps = {
  suggestType: SuggestType;
  basePath?: string;
  size?: number;
  sourceData?: Array<SourceDataItem>;
  placeholder: string;
  handleSearch: (previousState: AutocompleteFormState, formData: FormData) => AutocompleteFormState | Promise<AutocompleteFormState>;
};
const AutocompleteInnerForm = t0 => {
  const $ = _c(25);
  const {
    inputRef,
    inputProps,
    formState,
    placeholder
  } = t0;
  const status = useFormStatus();
  const {
    onChange,
    onBlur
  } = inputProps;
  const [term, setTerm] = useState(formState.term);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classes(pile({
      alignItems: "center"
    }));
    t2 = classes(occupy({
      inlineSize: "7 | betweenH3Sections"
    }), zIndex({
      zIndex: "1"
    }));
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== status.pending) {
    t3 = classes(icon({
      icon: status.pending ? hourglassIcon : searchIcon,
      blockSize: "3 | h1"
    }));
    $[2] = status.pending;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span className={t2}><i className={t3} /></span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onChange) {
    t5 = e => {
      setTerm(e.target.value);
      onChange(e);
    };
    $[6] = onChange;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = classes(textBlock({
      color: "positive:noHover"
    }), box({
      paddingBlock: "3 | h1",
      paddingInlineEnd: indentSize,
      paddingInlineStart: "7 | betweenH3Sections",
      backgroundColor: "negative",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "positive"
    }));
    t7 = addTestSelector("uiSearchInput");
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[10] !== inputRef || $[11] !== onBlur || $[12] !== placeholder || $[13] !== t5 || $[14] !== term) {
    t8 = <input onBlur={onBlur} onChange={t5} ref={inputRef} type="search" name="term" autoCapitalize="none" autoComplete="off" autoCorrect="off" spellCheck="false" value={term} placeholder={placeholder} className={t6} {...t7} />;
    $[10] = inputRef;
    $[11] = onBlur;
    $[12] = placeholder;
    $[13] = t5;
    $[14] = term;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t4 || $[17] !== t8) {
    t9 = <div className={t1}>{t4}{t8}</div>;
    $[16] = t4;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== formState.suggestions || $[20] !== status.pending) {
    t10 = !status.pending && <AutocompleteSuggestions suggestions={formState.suggestions} />;
    $[19] = formState.suggestions;
    $[20] = status.pending;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
};
export const AutocompleteClient = t0 => {
  const $ = _c(21);
  let placeholder;
  let props;
  if ($[0] !== t0) {
    ({
      placeholder,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = placeholder;
    $[2] = props;
  } else {
    placeholder = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      term: "",
      suggestions: []
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [formState, formAction] = useActionState(props.handleSearch, t1);
  const inputRef = useRef(null);
  const formRef = useRef(null);
  let t2;
  if ($[4] !== formState.term) {
    t2 = () => {
      if (formState.term !== "" && inputRef.current != null && formRef.current != null) {
        inputRef.current.value = "";
        formRef.current.requestSubmit();
      }
    };
    $[4] = formState.term;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useInteractOutside(formRef, t2);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      onChange: () => {
        if (formRef.current != null) {
          formRef.current.requestSubmit();
        }
      },
      action: formAction
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    formProps,
    inputProps
  } = useOnType(t3, 400);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classes(stack({
      rowGap: "2 | h2"
    }));
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classes(zIndex({
      zIndex: "1"
    }), position({
      position: "relative"
    }));
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== formState || $[10] !== inputProps || $[11] !== placeholder) {
    t6 = <AutocompleteInnerForm inputRef={inputRef} formState={formState} inputProps={inputProps} placeholder={placeholder} />;
    $[9] = formState;
    $[10] = inputProps;
    $[11] = placeholder;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== formProps || $[14] !== t6) {
    t7 = <form {...formProps} ref={formRef} action={formAction} id="autocompleteForm" className={t5} tabIndex={-1}>{t6}</form>;
    $[13] = formProps;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== formState.error) {
    t8 = formState.error != null && <ErrorMessage>{translateInline(appStateTranslations.noDataError)}</ErrorMessage>;
    $[16] = formState.error;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className={t4}>{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};